import { QUERY_KEYS } from "@/libraries/constant";
import { unix } from "@/libraries/dayjs-utc";
import { applyDataChanges } from "@/libraries/transform";
import type {
	BodyUpdateMemberSchema,
	GetMemberSyncChatParamsSchema,
	PostInvitationBodySchema,
} from "@/schemas";
import { memberService } from "@/services";
import { invitationService } from "@/services/invitation";
import { useSyncStore } from "@/stores/sync";
import type {
	GetCasesResponse,
	GetGroupsResponse,
	GetMemberAlertsResponse,
	GetMemberChatsResponse,
	MemberProfileResponse,
} from "@/types";
import type { GetMessageTotalUnreadResponseType } from "@/types/get-message-total-unread";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

const updateMember = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_MEMBER_UPDATE], {
		mutationFn: (payload: BodyUpdateMemberSchema) =>
			memberService.updateMember(payload),
		onMutate: async (payload) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MEMBER_PROFILE],
			});

			const existingUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);

			const optimisticData = {
				...existingUser,
				user: {
					...existingUser?.user,
					...payload,
				},
			};

			queryClient.setQueryData([QUERY_KEYS.GET_MEMBER_PROFILE], optimisticData);

			return { optimisticData, existingUser };
		},
		onSuccess: (res, _, ctx) => {
			queryClient.setQueryData([QUERY_KEYS.GET_MEMBER_PROFILE], (oldData) =>
				!oldData
					? oldData
					: {
							...ctx.optimisticData,
							user: {
								...ctx.optimisticData.user,
								...res.user,
							},
						},
			);
		},
		onError: (_, _payloa, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData(
				[QUERY_KEYS.GET_MEMBER_PROFILE],
				ctx?.existingUser,
			);
		},
	});
};

const getSyncChat = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_MEMBER_SYNC_CHAT], {
		retry: 2,
		mutationFn: (params: GetMemberSyncChatParamsSchema) =>
			memberService.syncChat(params),
		onSuccess: (res) => {
			queryClient.setQueryData<InfiniteData<GetMemberChatsResponse>>(
				[QUERY_KEYS.GET_MEMBER_CHATS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							users: applyDataChanges(p.users || [], res.update, res.delete),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			useSyncStore.setState({
				memberSyncChatLastUnix: unix(),
			});
		},
	});
};

const postInvitation = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_MEMBER_INVITATION], {
		retry: 2,
		mutationFn: (invitation: PostInvitationBodySchema) =>
			invitationService.invitations(invitation),
		onSuccess: async (res, { inviteable_type, message_id, status }) => {
			if (status === "accept") {
				switch (inviteable_type) {
					case "Group":
						queryClient.setQueryData<InfiniteData<GetGroupsResponse>>(
							[QUERY_KEYS.GET_GROUPS],
							(oldData) => ({
								...oldData,
								pages:
									oldData?.pages?.map((p) => ({
										...p,
										groups: [
											...(p.groups || []),
											...(res?.invitation?.inviteable
												? [res.invitation.inviteable as any]
												: []),
										],
									})) || [],
								pageParams: oldData?.pageParams || [1],
							}),
						);
						break;
					case "Case":
						queryClient.setQueryData<InfiniteData<GetCasesResponse>>(
							[QUERY_KEYS.GET_CASES, { status: "active" }],
							(oldData) => ({
								...oldData,
								pages:
									oldData?.pages?.map((p) => ({
										...p,
										cases: [
											...(p.cases || []),
											...(res?.invitation?.inviteable
												? [res.invitation.inviteable as any]
												: []),
										],
									})) || [],
								pageParams: oldData?.pageParams || [1],
							}),
						);
						queryClient.invalidateQueries({
							queryKey: [QUERY_KEYS.GET_ENCOUNTER],
							exact: false,
						});
						break;
					case "Team":
						queryClient.invalidateQueries({
							queryKey: [QUERY_KEYS.GET_TEAMS],
							exact: false,
						});
						break;
				}
			}
			queryClient.setQueryData<InfiniteData<GetMemberAlertsResponse>>(
				[QUERY_KEYS.GET_MY_ALERTS, { scope: "my_outstanding" }],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							messages: p.messages.filter((m) => m.id_db !== message_id),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetMessageTotalUnreadResponseType>(
				[QUERY_KEYS.GET_UNREADS],
				(oldData) =>
					!oldData
						? oldData
						: {
								...oldData,
								to_do_list: oldData.to_do_list - 1,
							},
			);
		},
	});
};

export const memberMutations = (queryClient: QueryClient) => {
	updateMember(queryClient);
	getSyncChat(queryClient);
	postInvitation(queryClient);
};
